import { z } from 'zod';
import { WORKFLOW_STAGES, workflowStageSchema } from '../constants/stages.js';
import { STAGE_MOVE_KINDS } from '../workflow/transitions.js';
import { isoDateStringSchema, objectIdSchema } from './common.js';
import { orderSchema, orderItemSchema } from './orders.js';

/**
 * The wire contract for the stage engine and the Kanban board.
 *
 * Two shapes here are worth reading closely.
 *
 * `stageMoveSchema` carries `expectedStage`. The board is optimistic — a card moves the instant
 * it is dropped — and two people work the same board from two phones. Sending the stage the
 * client BELIEVED the item was at lets the server answer 409 when somebody else moved it first,
 * so the losing card rolls back visibly instead of silently overwriting a colleague's work.
 *
 * `kanbanCard` is deliberately flat and small. A card is rendered a hundred at a time on a
 * phone; every field on it is one the card actually shows, and the photograph is referenced by
 * id rather than by URL because a signed URL would expire in the time a board stays open.
 */

export const STAGE_REASON_MAX_LENGTH = 300;

/** Required on every backward move: `classifyStageMove` refuses a blank one. */
export const stageReasonSchema = z
  .string()
  .trim()
  .min(3, 'Say why this garment is going back')
  .max(STAGE_REASON_MAX_LENGTH);

export const stageMoveSchema = z.object({
  toStage: workflowStageSchema,
  /** Mandatory for a backward move; ignored on an advance. */
  reason: stageReasonSchema.nullish(),
  /** True when work is being sent back because it failed, rather than to fix a mis-tap. */
  rework: z.boolean().default(false),
  /** What the client thought the item was at. A mismatch is a 409, not a silent overwrite. */
  expectedStage: workflowStageSchema.nullish(),
});
export type StageMoveInput = z.input<typeof stageMoveSchema>;

/**
 * The end-of-day pattern: select several cards and push them all one stage on.
 *
 * There is no `toStage` — each item advances to ITS OWN next enabled stage, because a selection
 * spanning cutting and stitching has no single destination. A bulk move is therefore always an
 * advance; sending work back is a per-garment decision with a per-garment reason.
 */
export const bulkAdvanceSchema = z.object({
  itemIds: z.array(objectIdSchema).min(1, 'Choose at least one garment').max(100),
});
export type BulkAdvanceInput = z.infer<typeof bulkAdvanceSchema>;

/**
 * "The customer collected the order." Every garment that is READY is handed over; anything
 * still in production is left where it is and reported back, because a bill marked delivered
 * while two blouses are still on the table is the argument this refusal exists to prevent.
 */
export const deliverOrderSchema = z.object({
  /** Restrict the hand-over to some garments. Omitted means every ready one. */
  itemIds: z.array(objectIdSchema).max(100).optional(),
});
export type DeliverOrderInput = z.infer<typeof deliverOrderSchema>;

// --- History ---------------------------------------------------------------------------------

export const stageMoveKindSchema = z.enum(STAGE_MOVE_KINDS);

/**
 * One line of a garment's stage history. `previousStageMs` is recorded at write time rather
 * than derived later: task 26's "average days per stage" and its bottleneck report are sums
 * over this field, and reconstructing durations from timestamps afterwards breaks the moment
 * an event is ever backfilled or an item is created mid-workflow by an import.
 */
export const stageEventSchema = z.object({
  id: objectIdSchema,
  itemId: objectIdSchema,
  orderId: objectIdSchema,
  orderNumber: z.string(),
  lineNumber: z.number().int().positive(),
  garmentTypeLabel: z.string(),
  fromStage: workflowStageSchema.nullable(),
  toStage: workflowStageSchema,
  kind: z.union([stageMoveKindSchema, z.enum(['booked', 'cancelled'])]),
  reason: z.string().nullable(),
  actorUserId: z.string().nullable(),
  /** Who moved it, as the shop knows them. Kept even if the person later leaves. */
  actorName: z.string().nullable(),
  at: isoDateStringSchema,
  /** How long the garment sat in the stage it just left. Null for the first event. */
  previousStageMs: z.number().int().nonnegative().nullable(),
});
export type StageEvent = z.infer<typeof stageEventSchema>;

export const stageHistorySchema = z.object({
  itemId: objectIdSchema,
  events: z.array(stageEventSchema),
});
export type StageHistory = z.infer<typeof stageHistorySchema>;

// --- Results ---------------------------------------------------------------------------------

export const stageMoveResultSchema = z.object({
  item: orderItemSchema,
  /** The order after the rollup — its stage and promised date may both have moved. */
  order: orderSchema,
  event: stageEventSchema,
});
export type StageMoveResult = z.infer<typeof stageMoveResultSchema>;

export const skippedStageMoveSchema = z.object({
  itemId: objectIdSchema,
  /** Machine-readable refusal from `classifyStageMove`, plus a sentence to show. */
  refusal: z.string(),
  message: z.string(),
});

export const bulkStageMoveResultSchema = z.object({
  moved: z.array(
    z.object({
      itemId: objectIdSchema,
      orderId: objectIdSchema,
      fromStage: workflowStageSchema,
      toStage: workflowStageSchema,
    }),
  ),
  /** Everything that could not move, and why. Never silently dropped. */
  skipped: z.array(skippedStageMoveSchema),
  /** The affected orders after their rollups, so the board can refresh without a refetch. */
  orders: z.array(orderSchema),
});
export type BulkStageMoveResult = z.infer<typeof bulkStageMoveResultSchema>;

// --- The board -------------------------------------------------------------------------------

/** How late a garment is, decided once on the server against the shop's own day boundary. */
export const DUE_STATES = ['overdue', 'today', 'upcoming', 'none'] as const;
export type DueState = (typeof DUE_STATES)[number];
export const dueStateSchema = z.enum(DUE_STATES);

/** The due windows a shop filters the board by. */
export const KANBAN_DUE_FILTERS = ['all', 'overdue', 'today', 'week'] as const;
export type KanbanDueFilter = (typeof KANBAN_DUE_FILTERS)[number];

export const kanbanQuerySchema = z.object({
  due: z.enum(KANBAN_DUE_FILTERS).default('all'),
  customerId: objectIdSchema.optional(),
  garmentTypeKey: z.string().trim().max(60).optional(),
  /** Order number, customer name or phone. */
  q: z.string().trim().max(120).optional(),
  priorityOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
  /**
   * Cards per column. A column with more says so — `KanbanColumn.total` is the true count, and
   * the board tells the shop what it is not showing rather than pretending the column is short.
   */
  limit: z.coerce.number().int().min(1).max(200).default(60),
});
export type KanbanQuery = z.infer<typeof kanbanQuerySchema>;

export const kanbanCardSchema = z.object({
  itemId: objectIdSchema,
  orderId: objectIdSchema,
  orderNumber: z.string(),
  lineNumber: z.number().int().positive(),
  customerId: objectIdSchema,
  customerName: z.string(),
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  quantity: z.number().int().positive(),
  stage: workflowStageSchema,
  promisedDate: isoDateStringSchema.nullable(),
  dueState: dueStateSchema,
  priority: z.boolean(),
  /** When this garment entered its current stage — "3 days in stitching" on the card. */
  stageEnteredAt: isoDateStringSchema,
  /**
   * The design reference photograph, by id. The board asks the media endpoint for a signed URL
   * per visible card; embedding one here would have expired before a board left open all
   * morning was next looked at.
   */
  thumbnailMediaId: objectIdSchema.nullable(),
  /** Who is stitching it — the open assignment on this garment, or null while nobody has it. */
  assigneeName: z.string().nullable(),
  /**
   * A karigar has raised a problem on this garment and nobody has cleared it (task 20).
   *
   * It is a boolean rather than the problem itself because that is all the card does with it:
   * mark the garment so the owner opens it. The kind, the note and who raised it are on the
   * assignment, one tap away, and would be three more fields on a shape rendered a hundred at a
   * time on a phone.
   */
  hasProblem: z.boolean(),
  /**
   * The outside unit this garment is with (task 29), or null while it is on the premises.
   *
   * A field on the card rather than a stage of its own: the board's job here is to stop an
   * off-premises garment reading as a stalled one, and a name plus the days it has been out
   * does that without adding a workflow stage that no rate card, assignment or report knows
   * what to do with.
   */
  outsourcedTo: z.string().nullable(),
  outsourcedDays: z.number().int().nullable(),
});
export type KanbanCard = z.infer<typeof kanbanCardSchema>;

export const kanbanColumnSchema = z.object({
  stage: workflowStageSchema,
  /** This shop's word for the stage. No screen may hardcode one. */
  label: z.string(),
  cards: z.array(kanbanCardSchema),
  /** Cards matching the filter in this stage, which may exceed `cards.length`. */
  total: z.number().int().nonnegative(),
});
export type KanbanColumn = z.infer<typeof kanbanColumnSchema>;

export const kanbanBoardSchema = z.object({
  columns: z.array(kanbanColumnSchema),
  /** This shop's workflow, in order — the columns follow it exactly. */
  enabledStages: z.array(workflowStageSchema),
  /** Answers "what is late?" without counting cards. */
  totals: z.object({
    active: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    dueToday: z.number().int().nonnegative(),
  }),
  limit: z.number().int().positive(),
});
export type KanbanBoard = z.infer<typeof kanbanBoardSchema>;

// --- The shop's stage configuration, as `/settings/stages` serves it -------------------------

export const resolvedStageSchema = z.object({
  key: workflowStageSchema,
  ordinal: z.number().int(),
  label: z.string(),
  renamed: z.boolean(),
  enabled: z.boolean(),
  required: z.boolean(),
  progressive: z.boolean(),
  terminal: z.boolean(),
});
export type ResolvedStageView = z.infer<typeof resolvedStageSchema>;

/** One garment type's own route: which of the shop's stages it walks, in canonical order. */
export const garmentStageFlowViewSchema = z.object({
  garmentTypeKey: z.string(),
  stages: z.array(workflowStageSchema),
});
export type GarmentStageFlowView = z.infer<typeof garmentStageFlowViewSchema>;

export const stageConfigurationSchema = z.object({
  /**
   * Per-garment routes. Sparse: only the garment types this shop has customised appear, and
   * everything else walks `enabledStages`. Defaulted so a response recorded before this
   * shipped still parses.
   */
  flows: z.array(garmentStageFlowViewSchema).default([]),
  stages: z.array(resolvedStageSchema).length(WORKFLOW_STAGES.length),
  enabledStages: z.array(workflowStageSchema),
  optionalStages: z.array(workflowStageSchema),
});
export type StageConfigurationView = z.infer<typeof stageConfigurationSchema>;
